import { computed, inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  finalize,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  throwError,
} from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { LocalStorageKeyEnum } from '../enums';
import { AddressLevelType, Balance, User } from '@models';
import { ApiService } from './api-service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly authService = inject(AuthService);
  private readonly apiService = inject(ApiService);
  isUserLoaded$ = new BehaviorSubject<boolean>(false);
  reloadUser$ = new Subject<void>();

  user = toSignal(
    merge(this.authService.tokenChanges$, this.reloadUser$).pipe(
      startWith(null),
      debounceTime(10),
      switchMap(() => (this.authService.token ? this.getUserRequest() : of(null))),
    ),
    { initialValue: null },
  );
  name = computed(() => this.user()?.name);
  greetingName = computed(() => {
    const user = this.user();
    if (!user?.name) return '';

    const { firstName, lastName } = user.name;
    return user.isDisplayNameSubstituted ? `${firstName} ${lastName}`.trim() : firstName;
  });
  telephoneNumber = computed(() => this.user()?.telephoneNumber);
  emailAddress = computed(() => this.user()?.emailAddress);
  isValidAddress = computed(() => {
    return this.user()?.isValidAddress ?? null;
  });
  addresses = computed(() => this.user()?.addresses);
  primaryAddress = computed(() =>
    this.addresses()?.find((a) => a.addressUsageLevelText === AddressLevelType.CustomerPrimary),
  );
  balance = computed(() => this.user()?.balance as Balance);
  conversionRate = computed(() => this.balance()?.conversionRate ?? 0);
  points = computed(() => this.balance()?.points ?? 0);
  rpcCode = computed(() => this.user()?.rpcCode);
  disablePointsAccelerator = computed(() => this.user()?.disablePointsAccelerator ?? false);
  cash = computed(() => Math.floor((this.points() / this.conversionRate()) * 100) / 100);
  canRedeemPoints = computed(() => this.balance()?.isRedemptionAllowed ?? true);

  private getUserRequest(): Observable<User> {
    return this.apiService.get<User>('user').pipe(
      finalize(() => this.isUserLoaded$.next(true)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          this.authService.logout();
        }
        return throwError(() => error);
      }),
    );
  }

  updateUserInfo(): void {
    this.reloadUser$.next();
  }

  setShoppingJourneyId(id: string): void {
    this.localStorageService.setItem(LocalStorageKeyEnum.SHOPPING_JOURNEY_ID, id);
  }
}
